"use client";

import { useEffect, useRef, useState, type CSSProperties, type FormEvent } from "react";
import { ArrowUpRight, Check, Globe2, Pause, RotateCcw, Search } from "lucide-react";
import { normalizeWebsite } from "../seo-website-input";
import type { GoogleVisibilityCopy } from "./google-visibility-copy";
import styles from "./google-visibility.module.scss";

type Props = { copy: GoogleVisibilityCopy["demo"]; preview?: boolean; animate?: boolean };

/** Local-only illustrative ranking sequence. No domain is sent to a service. */
export function CcGoogleRankClimb({ copy, preview = false, animate = true }: Props) {
  const [step, setStep] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [input, setInput] = useState("");
  const [domain, setDomain] = useState(copy.defaultDomain);
  const [error, setError] = useState(false);
  const frameRef = useRef<HTMLDivElement>(null);
  const rank = copy.steps[preview && !animate ? copy.steps.length - 1 : step].rank;
  const otherRanks = [1, 4, 6, 10];
  const targetSlot = otherRanks.filter((value) => value < rank).length;

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame || !animate || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      setPlaying(true);
      observer.disconnect();
    }, { threshold: 0.5 });
    observer.observe(frame);
    return () => observer.disconnect();
  }, [animate]);

  useEffect(() => {
    if (!playing || !animate) return;
    const timer = window.setTimeout(() => {
      if (step < copy.steps.length - 1) setStep(step + 1);
      else if (preview) setStep(0);
      else setPlaying(false);
    }, step === copy.steps.length - 1 && preview ? 4200 : 1800);
    return () => window.clearTimeout(timer);
  }, [playing, step, copy.steps.length, preview, animate]);

  function replay() {
    setStep(0);
    setPlaying(!window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const website = normalizeWebsite(input);
    if (!website) {
      setError(true);
      return;
    }
    setDomain(website.split("/")[0]);
    setError(false);
    replay();
  }

  return (
    <div className={styles.climb} ref={frameRef} data-preview={preview || undefined}>
      <div className={styles.stage}>
        <div className={styles.searchBar}><Search size={17} aria-hidden="true" /><span>{copy.query}</span><small>{copy.queryLabel}</small></div>
        <div className={styles.perspective} aria-hidden="true">
          <div className={styles.board}>
            {copy.others.map((result, index) => {
              const resultRank = otherRanks[index];
              const slot = index + (resultRank > rank ? 1 : 0);
              return <div className={styles.result} key={result.domain} style={{ "--slot": slot } as CSSProperties}>
                <span className={styles.rankNumber}>{String(resultRank).padStart(2, "0")}</span>
                <div className={styles.resultBody}><div className={styles.resultDomain}><Globe2 size={18} /><span>{result.domain}<small>https://{result.domain}</small></span></div><strong>{result.title}</strong><div className={styles.skeleton}><i /><i /></div></div>
              </div>;
            })}
            <div className={`${styles.result} ${styles.yourResult}`} data-rank-highlight style={{ "--slot": targetSlot } as CSSProperties}>
              <span className={styles.rankNumber}>{String(rank).padStart(2, "0")}</span>
              <div className={styles.resultBody}>
                <div className={styles.resultDomain}><span className={styles.siteIcon}><Globe2 size={18} /></span><span>{domain}<small>https://{domain}</small></span><span className={styles.yourLabel}>{copy.yourSite}</span></div>
                <strong>{copy.resultTitle}</strong><p>{copy.resultText}</p>
              </div>
              <span className={styles.corner} />
            </div>
          </div>
        </div>
        <div className={styles.rankBadge}><ArrowUpRight size={24} aria-hidden="true" /><span><small>{copy.position}</small><strong>#{rank}</strong></span></div>
      </div>
      {!preview && <><div className={styles.controls}>
        <div className={styles.controlHeader}><span className={styles.micro}><i />{copy.label}</span><button type="button" onClick={playing ? () => setPlaying(false) : replay}>{playing ? <Pause size={14} /> : <RotateCcw size={14} />}{playing ? copy.pause : copy.replay}</button></div>
        <div className={styles.stepButtons} aria-label={copy.label}>
          {copy.steps.map((item, index) => <button type="button" key={item.rank} aria-pressed={index === step} onClick={() => { setPlaying(false); setStep(index); }}><span>{index < step ? <Check size={13} aria-hidden="true" /> : String(index + 1).padStart(2, "0")}</span>{item.label}</button>)}
        </div>
        <div className={styles.stepDescription} aria-live="polite"><strong>{copy.steps[step].title}</strong><p>{copy.steps[step].text}</p><span className={styles.srOnly}>{copy.position} {rank}. {domain}</span></div>
        <form data-analytics-tool="rank_demo" onSubmit={submit} className={styles.domainForm}>
          <label htmlFor="google-example-domain">{copy.domainLabel}</label>
          <div><Globe2 size={17} aria-hidden="true" /><input id="google-example-domain" type="text" inputMode="url" autoComplete="off" maxLength={253} placeholder={copy.placeholder} value={input} onChange={(event) => setInput(event.target.value)} aria-invalid={error} aria-describedby={error ? "google-demo-error google-demo-note" : "google-demo-note"} /><button type="submit">{copy.submit}<ArrowUpRight size={17} aria-hidden="true" /></button></div>
          {error && <p id="google-demo-error" role="alert">{copy.error}</p>}
        </form>
      </div>
      <p className={styles.demoNote} id="google-demo-note">{copy.note}</p></>}
    </div>
  );
}
